/**
 * Scan Agent - Global Library Browser
 * 
 * Searchable, filterable catalog of all scanned_books.
 * Shows quality_status + whether the book is in any parent's library (published).
 * Agents use this to check if a book already exists before scanning.
 * 
 * Route: /scan-agent/library
 */

import React, { useState, useEffect, useCallback, useMemo, lazy, Suspense } from 'react';
import { Link } from 'react-router-dom';
import {
  Search,
  BookOpen,
  RefreshCw,
  Pencil,
  Palette,
  Users,
  X,
  Eye,
} from 'lucide-react';
import { supabase } from '@/lib/supabase/client';
import { Card, Badge, Spinner, EmptyState } from '@/components/ui';

const LibraryRendering = lazy(() => import('./library-rendering'));

// =============================================================================
// TYPES
// =============================================================================

interface LibraryBook {
  id: string;
  title: string;
  author: string | null;
  isbn: string | null;
  cover_image_url: string | null;
  quality_status: string | null;
  total_pages: number | null;
  binding: string | null;
  work_group: string | null;
  created_at: string | null;
  parent_count: number;
}

type StatusFilter = 'all' | 'published' | 'ready' | 'needs_review' | 'processing' | 'failed';

// =============================================================================
// PURE HELPERS
// =============================================================================

export function filterLibraryBooks(
  books: LibraryBook[],
  query: string,
  statusFilter: StatusFilter
): LibraryBook[] {
  let filtered = books;

  if (statusFilter !== 'all') {
    filtered = filtered.filter(book => {
      if (statusFilter === 'published') return book.parent_count > 0;
      if (statusFilter === 'processing') return book.quality_status === 'processing' || book.quality_status === 'reconstructing';
      return book.quality_status === statusFilter;
    });
  }

  if (!query) return filtered;
  const q = query.toLowerCase();
  return filtered.filter(book =>
    book.title.toLowerCase().includes(q) ||
    book.author?.toLowerCase().includes(q) ||
    book.isbn?.toLowerCase().includes(q)
  );
}

export function computeLibraryStats(books: LibraryBook[]) {
  return {
    total: books.length,
    published: books.filter(b => b.parent_count > 0).length,
    ready: books.filter(b => b.quality_status === 'ready').length,
    processing: books.filter(b => b.quality_status === 'processing' || b.quality_status === 'reconstructing').length,
    needsReview: books.filter(b => b.quality_status === 'needs_review').length,
    failed: books.filter(b => b.quality_status === 'failed').length,
  };
}

// =============================================================================
// COMPONENT
// =============================================================================

export function ScanAgentLibrary() {
  const [books, setBooks] = useState<LibraryBook[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [view, setView] = useState<'catalog' | 'rendering'>('catalog');

  const fetchBooks = useCallback(async () => {
    setRefreshing(true);
    try {
      // Fetch books with count of parents who have them in their library
      const { data: booksData, error: booksError } = await supabase
        .from('scanned_books')
        .select('id, title, author, isbn, cover_image_url, quality_status, total_pages, binding, work_group, created_at')
        .is('deleted_at', null)
        .order('created_at', { ascending: false });

      if (booksError) throw booksError;

      // Get parent counts per book from junction table
      const bookIds = (booksData || []).map(b => b.id);
      const parentCounts = new Map<string, number>();

      if (bookIds.length > 0) {
        const { data: libData } = await supabase
          .from('parent_book_library')
          .select('book_id')
          .in('book_id', bookIds);

        if (libData) {
          for (const row of libData) {
            parentCounts.set(row.book_id, (parentCounts.get(row.book_id) || 0) + 1);
          }
        }
      }

      const enriched: LibraryBook[] = (booksData || []).map(b => ({
        ...b,
        parent_count: parentCounts.get(b.id) || 0,
      }));

      setBooks(enriched);
    } catch (error) {
      console.error('Error fetching global library:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchBooks();
  }, [fetchBooks]);

  const filteredBooks = useMemo(
    () => filterLibraryBooks(books, searchQuery, statusFilter),
    [books, searchQuery, statusFilter]
  );

  const stats = useMemo(() => computeLibraryStats(books), [books]);

  const workGroupCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const book of books) {
      if (book.work_group) {
        counts.set(book.work_group, (counts.get(book.work_group) || 0) + 1);
      }
    }
    return counts;
  }, [books]);

  const getStatusBadge = (book: LibraryBook) => {
    const badges: React.ReactNode[] = [];

    switch (book.quality_status) {
      case 'ready':
        badges.push(<Badge key="status" variant="success">Ready</Badge>);
        break;
      case 'needs_review':
        badges.push(<Badge key="status" variant="warning">Needs Review</Badge>);
        break;
      case 'failed':
        badges.push(<Badge key="status" variant="error">Failed</Badge>);
        break;
      case 'processing':
      case 'reconstructing':
        badges.push(<Badge key="status" variant="info">Processing</Badge>);
        break;
      default:
        badges.push(<Badge key="status">{book.quality_status || 'Unknown'}</Badge>);
    }

    if (book.binding) {
      badges.push(
        <span key="binding" className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-black/[0.05] text-[#636366]">
          {book.binding}
        </span>
      );
    }

    if (book.parent_count > 0) {
      badges.push(
        <span key="published" className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-[#BF5AF2]/15 text-[#BF5AF2]">
          <Users className="w-3 h-3" />
          {book.parent_count} {book.parent_count === 1 ? 'parent' : 'parents'}
        </span>
      );
    }

    return badges;
  };

  const filterTabs: { key: StatusFilter; label: string; count: number; color: string }[] = [
    { key: 'all', label: 'All', count: stats.total, color: 'text-[#636366]' },
    { key: 'published', label: 'Published', count: stats.published, color: 'text-[#BF5AF2]' },
    { key: 'ready', label: 'Ready', count: stats.ready, color: 'text-[#30D158]' },
    { key: 'needs_review', label: 'Needs Review', count: stats.needsReview, color: 'text-[#FF9F0A]' },
    { key: 'processing', label: 'Processing', count: stats.processing, color: 'text-[#0A84FF]' },
    { key: 'failed', label: 'Failed', count: stats.failed, color: 'text-[#FF453A]' },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* View tabs */}
      <div className="flex gap-1">
        {([
          { key: 'catalog' as const, label: 'Catalog', icon: <BookOpen className="w-3.5 h-3.5" /> },
          { key: 'rendering' as const, label: 'Rendering Preview', icon: <Eye className="w-3.5 h-3.5" /> },
        ]).map(tab => (
          <button
            key={tab.key}
            onClick={() => setView(tab.key)}
            className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-colors ${
              view === tab.key
                ? 'bg-[#111] text-white'
                : 'bg-black/[0.03] text-[#636366] hover:bg-black/[0.06]'
            }`}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      {view === 'rendering' ? (
        <Suspense fallback={<div className="flex justify-center py-20"><Spinner size="lg" /></div>}>
          <LibraryRendering />
        </Suspense>
      ) : (
      <>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-[#1C1C1E]">Global Library</h1>
          <p className="text-sm text-[#8E8E93]">
            {stats.total} book{stats.total !== 1 ? 's' : ''} in catalog
            {stats.total > 0 && (
              <span className="ml-2">
                <span className="text-[#BF5AF2]">{stats.published} published</span>
                <span className="text-[#30D158]"> · {stats.ready} ready</span>
                {stats.needsReview > 0 && <span className="text-[#FF9F0A]"> · {stats.needsReview} review</span>}
                {stats.processing > 0 && <span className="text-[#0A84FF]"> · {stats.processing} processing</span>}
                {stats.failed > 0 && <span className="text-[#FF453A]"> · {stats.failed} failed</span>}
              </span>
            )}
          </p>
        </div>
        <button
          onClick={fetchBooks}
          disabled={refreshing}
          className="flex items-center gap-2 px-4 py-2 rounded-xl bg-black/[0.03] text-[#636366] hover:bg-black/[0.05] transition-all disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          <span className="hidden sm:inline">Refresh</span>
        </button>
      </div>

      {/* Filter tabs */}
      <div className="flex flex-wrap gap-2">
        {filterTabs.map(tab => (
          <button
            key={tab.key}
            onClick={() => setStatusFilter(tab.key)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
              statusFilter === tab.key
                ? `bg-black/[0.05] ${tab.color}`
                : 'bg-black/[0.03] text-[#8E8E93] hover:bg-black/[0.05] hover:text-[#636366]'
            }`}
          >
            {tab.label}
            {tab.count > 0 && <span className="ml-1.5 opacity-70">{tab.count}</span>}
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="relative max-w-lg">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#8E8E93]" />
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search by title, author, or ISBN..."
          className="w-full pl-10 pr-10 py-3 bg-black/[0.03] border border-black/[0.08] rounded-xl text-[#1C1C1E] placeholder-[#AEAEB2] focus:outline-none focus:ring-2 focus:ring-[#0A84FF]/50 focus:border-[#0A84FF]/30 text-sm"
          autoFocus
        />
        {searchQuery && (
          <button
            onClick={() => setSearchQuery('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-[#8E8E93] hover:text-[#1C1C1E] transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Results */}
      {filteredBooks.length === 0 ? (
        <Card className="p-12">
          <EmptyState
            icon={<BookOpen className="w-8 h-8 text-[#8E8E93]" />}
            title={searchQuery ? 'No matching books' : statusFilter !== 'all' ? 'No books in this category' : 'Library is empty'}
            description={searchQuery
              ? `No books match "${searchQuery}". Safe to scan a new copy.`
              : statusFilter !== 'all' ? 'Try a different filter or scan a new book.'
              : 'No books have been scanned yet.'
            }
          />
        </Card>
      ) : (
        <>
          {searchQuery && (
            <p className="text-sm text-[#8E8E93]">
              {filteredBooks.length} result{filteredBooks.length !== 1 ? 's' : ''}
            </p>
          )}
          <div className="space-y-2">
            {filteredBooks.map((book) => (
              <Card key={book.id} padding="none" className="px-4 py-3">
                <div className="flex items-center gap-4">
                  {/* Cover thumbnail */}
                  {book.cover_image_url ? (
                    <img
                      src={book.cover_image_url}
                      alt={book.title}
                      className="w-10 h-14 object-cover rounded flex-shrink-0"
                    />
                  ) : (
                    <div className="w-10 h-14 bg-black/[0.05] rounded flex items-center justify-center flex-shrink-0">
                      <BookOpen className="w-5 h-5 text-[#AEAEB2]" />
                    </div>
                  )}

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                      <h3 className="font-medium text-[#1C1C1E] truncate">{book.title}</h3>
                      {getStatusBadge(book)}
                    </div>
                    <div className="flex flex-wrap items-center gap-3 text-xs text-[#8E8E93]">
                      {book.author && <span>{book.author}</span>}
                      {book.isbn && <span>ISBN: {book.isbn}</span>}
                      {book.total_pages != null && book.total_pages > 0 && <span>{book.total_pages} pages</span>}
                      {book.created_at && <span>{new Date(book.created_at).toLocaleDateString()}</span>}
                      {book.work_group && (workGroupCounts.get(book.work_group) || 0) > 1 && (
                        <span className="text-[#BF5AF2]">
                          {workGroupCounts.get(book.work_group)} editions
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {(book.quality_status === 'ready' || book.quality_status === 'turn_codes_pending') && (
                      <Link
                        to={`/admin/content/review?book=${book.id}`}
                        className="p-2 text-[#BF5AF2] hover:bg-[#BF5AF2]/10 rounded-lg transition-colors"
                        title="Review turn codes"
                      >
                        <Palette className="w-4 h-4" />
                      </Link>
                    )}
                    <Link
                      to={`/scan-agent/edit/${book.id}`}
                      className="p-2 text-[#0A84FF] hover:bg-[#0A84FF]/10 rounded-lg transition-colors"
                      title="Edit content"
                    >
                      <Pencil className="w-4 h-4" />
                    </Link>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </>
      )}
      </>
      )}
    </div>
  );
}

export default ScanAgentLibrary;
